import styled from 'styled-components';
import * as yup from 'yup';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { Box } from '@mui/material';
import { AnswerSelector } from './components/AnswerSelector';
import { OrderNumberForm } from './components/OrderNumberForm';
import { SubjectSelector } from './components/SubjectSelector';
import { MessageForm } from './components/MessageForm';
import { NameForm } from './components/NameForm';
import { AddressForm } from './components/AddressForm';
import { StoreSelectForm } from './components/StoreSelectForm';
import { StoreSelectFormOptional } from './components/StoreSelectFormOptional';
import { PrivacyMessage } from './components/PrivacyMessage';
import { PrevStep } from './components/PrevStep';
import {
    breakpoints,
    colors,
    emailSubjects,
    formFields,
    stepComponentNames,
    stepNames,
} from './constants';
import { IOption, IAnswerSelectStep, IPrevStep } from './types';
import {
    getTouchedFields,
    getValidationSchema,
    postDataLayerEvent,
} from './utils';
import { steps } from './data';
import { getDynamicTitle } from './utils/getDynamicTitle';

const AppContainer = styled.div``;

interface IProps {
    className?: string;
}

export type TFormValues = typeof initialValues;

const initialValues = {
    [formFields.company]: 'pnl',
    [formFields.subject]: '',
    [formFields.orderNumber]: '',
    [formFields.salutation]: '',
    [formFields.firstName]: '',
    [formFields.lastNamePrefix]: '',
    [formFields.lastName]: '',
    [formFields.street]: '',
    [formFields.houseNumber]: '',
    [formFields.houseNumberAddition]: '',
    [formFields.zipcode]: '',
    [formFields.city]: '',
    [formFields.phoneNumber]: '',
    [formFields.customerEmail]: '',
    [formFields.message]: '',
    [formFields.store]: '',
    [formFields.sendToDeliveryService]: '',
    [formFields.contactPreference]: '',
};

const BaseApp = ({ className }: IProps) => {
    const [step, setStep] = useState(stepNames.subscriptionQuestion);
    const [file, setFile] = useState<File | null>(null);
    const [nextSteps, setNextSteps] = useState<string[]>([]);
    const [prevSteps, setPrevSteps] = useState<IPrevStep[] | []>([]);
    const [onlineOrder, setOnlineOrder] = useState(false);
    const [isAvgSubject, setIsAvgSubject] = useState(false);
    const [formSubmittable, setFormSubmittable] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [loading, setLoading] = useState(false);
    const currentStep = steps[step];

    const validationSchemaShape = getValidationSchema(
        currentStep.stepComponentName,
        onlineOrder,
        isAvgSubject
    );

    const currentValidationSchema = yup
        .object()
        .shape(validationSchemaShape ?? {});

    const formik = useFormik({
        initialValues,
        validationSchema: currentValidationSchema,
        onSubmit: (formValues) => {
            if (
                !process.env.REACT_APP_CONTACT_FORM_MAILER_API_URL ||
                !isValid ||
                !formSubmittable
            )
                return;

            setLoading(true);

            const formData = new FormData();

            Object.keys(formValues).forEach((key) => {
                formData.append(
                    key,
                    formValues[key as keyof TFormValues].toString()
                );
            });

            if (file) {
                formData.append('attachment', file);
            }

            axios
                .post(
                    process.env.REACT_APP_CONTACT_FORM_MAILER_API_URL,
                    formData
                )
                .then(() => {
                    const eventAction = sendToDeliveryService
                        ? 'Over contactlenzenabonnement'
                        : 'Ander onderwerp';

                    postDataLayerEvent(
                        'Contactform sent',
                        eventAction,
                        subject
                    );

                    const redirectObj = window.top ?? window;
                    redirectObj.location.href = `${process.env.REACT_APP_SUCCES_URL_PNL}`;
                })
                .catch((error) => {
                    console.log(error);
                    setSubmitError('Er is iets misgegaan bij het verzenden');
                    setLoading(false);
                });
        },
    });

    const {
        values: { sendToDeliveryService, store, subject },
        values,
        errors: formikErrors,
        isValid,
        touched,
        handleSubmit,
        setFieldValue,
        setTouched,
        validateForm,
    } = formik;

    const stepFormValues = {
        [stepComponentNames.nameForm]: [
            // Add array clusters to map over and display answers on separate lines
            [
                values.salutation,
                values.firstName,
                values.lastNamePrefix,
                values.lastName,
            ],
            [values.customerEmail],
            [values.phoneNumber],
        ],
        [stepComponentNames.addressForm]: [
            [values.street, values.houseNumber, values.houseNumberAddition],
            [values.zipcode, values.city],
        ],
        [stepComponentNames.storeSelect]: [[store]],
        [stepComponentNames.storeSelectOptional]: [[store]],
        [stepComponentNames.orderNumberForm]: [[values.orderNumber]],
    };

    const getFieldError = (fieldName: keyof TFormValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    // Reset fields to initial values
    const resetFields = (formFields: string[]) =>
        formFields.forEach((formField) => {
            setFieldValue(formField, initialValues[formField]);
        });

    const getAnswerStrings = () => {
        const currentAnswers = stepFormValues[currentStep.stepComponentName];
        const currentFieldsHaveValues = !!currentAnswers
            .flat()
            .find((value) => value !== '');

        return currentFieldsHaveValues ? currentAnswers : '-';
    };

    const handleSubjectSelect = (selectedSubject: string) => {
        const isAvgSubject = [
            emailSubjects.viewPrescriptionInfo,
            emailSubjects.viewPersonalInfo,
            emailSubjects.movePersonalInfo,
            emailSubjects.deletePersonalInfo,
        ].includes(selectedSubject);

        isAvgSubject ? setIsAvgSubject(true) : setIsAvgSubject(false);

        setFieldValue(formFields.subject, selectedSubject);
    };

    const setCurrentFieldsTouched = (formFields: string[]) => {
        const touchedFields = getTouchedFields(formFields);

        setTouched(touchedFields);
    };

    const handleStepButtonClick = (answer: IOption) => {
        // Touch current fields, otherwise errors will not show if !isValid
        if (currentStep.formFields && !answer.skipValidation) {
            setCurrentFieldsTouched(currentStep.formFields);
        }

        // Do nothing if form is not valid (field errors will show)
        if (currentStep.formFields && !isValid && !answer.skipValidation)
            return;

        // Reset current fields if step is skipped
        if (currentStep.formFields && answer.skipValidation)
            resetFields(currentStep.formFields);

        if (answer.onlineOrder !== undefined)
            setOnlineOrder(answer.onlineOrder);

        if (answer.subject) handleSubjectSelect(answer.subject);

        if (answer.sendToDeliveryService !== undefined)
            setFieldValue(
                formFields.sendToDeliveryService,
                // Convert boolean to string (booleans are not sendable with FormData)
                answer.sendToDeliveryService ? 'true' : ''
            );

        // Add answers to prevSteps for edit functionality
        setPrevSteps([
            ...prevSteps,
            {
                id: currentStep.id,
                step,
                nextSteps,
                title: currentStep.titleSummary,
                answer:
                    currentStep.formFields && !answer.skipValidation
                        ? getAnswerStrings()
                        : answer.title,
                formFields: currentStep.formFields,
            },
        ]);

        // Update nextSteps and current step based on answer
        if (answer.nextSteps) {
            const nextStep = answer.nextSteps[0];

            setNextSteps(answer.nextSteps);
            setTouched({});
            setStep(nextStep);
        } else {
            // Otherwise keep iterating through existing nextSteps
            const nextStepIndex = nextSteps.indexOf(step) + 1;

            setTouched({});
            setStep(nextSteps[nextStepIndex]);
        }
    };

    const handleEditButtonClick = (selectedStep: string) => {
        const selectedPrevStepIndex = prevSteps.findIndex(
            (prevStep) => prevStep.step === selectedStep
        );

        const prevStepsToRemove = prevSteps.filter(
            (_, index) => index > selectedPrevStepIndex
        );

        // Reset current form fields
        if (currentStep.formFields) {
            resetFields(currentStep.formFields);
        }

        // Reset previous form fields
        prevStepsToRemove.forEach((prevStep) => {
            if (prevStep.formFields) {
                resetFields(prevStep.formFields);
            }
        });

        // Remove all prev steps after selected step
        const newPrevSteps = prevSteps.slice(0, selectedPrevStepIndex);

        setNextSteps(prevSteps[selectedPrevStepIndex].nextSteps);
        setPrevSteps(newPrevSteps);
        setStep(selectedStep);
        setSubmitError('');
    };

    useEffect(() => {
        if (!validationSchemaShape) return;

        // Quick fix to prevent pressing 'Enter' submitting invalid form
        // before arriving at the last step
        if (currentStep.stepComponentName === stepComponentNames.messageForm) {
            setFormSubmittable(true);
        } else {
            setFormSubmittable(false);
        }

        // Because this is a multi-step form, revalidate every step or if touched state has changed
        // otherwise errors won't show correctly
        validateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, touched]);

    const commonStepProps = {
        options: currentStep.options,
        onStepButtonClick: handleStepButtonClick,
    };

    const commonFormProps = {
        formValues: formik.values,
        setFieldValue,
        getFieldError,
    };

    const isDeleteSubject = subject === emailSubjects.deletePersonalInfo;

    const getTitle = () =>
        (currentStep as IAnswerSelectStep).title
            ? (currentStep as IAnswerSelectStep).title
            : getDynamicTitle(step, values.subject);

    return (
        <div className={className}>
            <AppContainer>
                <h1>Wat kunnen we voor je doen?</h1>

                <Box mb={4}>
                    Door dit contactformulier in te vullen gaat u akkoord met
                    onze{' '}
                    <a
                        href="https://www.pearle.nl/home/disclaimer-privacy"
                        rel="noreferrer"
                        target="_blank"
                    >
                        privacy policy
                    </a>
                    .
                </Box>

                {prevSteps.map((prevStep) => (
                    <AnimatePresence key={prevStep.id}>
                        <motion.div
                            animate={{ opacity: 1 }}
                            initial={{ opacity: 0 }}
                            key={prevStep.id}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                            <PrevStep
                                answers={prevStep.answer}
                                stepName={prevStep.step}
                                title={prevStep.title}
                                onEditButtonClick={handleEditButtonClick}
                            />
                        </motion.div>
                    </AnimatePresence>
                ))}
                <form onSubmit={handleSubmit}>
                    <AnimatePresence mode="popLayout">
                        <motion.div
                            animate={{ opacity: 1 }}
                            initial={{ opacity: 0 }}
                            key={currentStep.id}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                            {currentStep.stepComponentName ===
                                stepComponentNames.subjectSelect && (
                                <SubjectSelector
                                    options={
                                        sendToDeliveryService
                                            ? currentStep.options.slice(0, -4)
                                            : currentStep.options
                                    }
                                    setFieldValue={setFieldValue}
                                    onStepButtonClick={handleStepButtonClick}
                                />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.answerSelect && (
                                <AnswerSelector
                                    title={getTitle()}
                                    {...commonStepProps}
                                />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.privacyMessage && (
                                <PrivacyMessage />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.orderNumberForm && (
                                <OrderNumberForm
                                    getFieldError={getFieldError}
                                    onlineOrder={onlineOrder}
                                    setFieldValue={setFieldValue}
                                    {...commonStepProps}
                                />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.nameForm && (
                                <NameForm
                                    {...commonStepProps}
                                    {...commonFormProps}
                                    showDeleteDisclaimer={isDeleteSubject}
                                />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.addressForm && (
                                <AddressForm
                                    formFields={currentStep.formFields}
                                    setCurrentFieldsTouched={
                                        setCurrentFieldsTouched
                                    }
                                    showDeleteDisclaimer={isDeleteSubject}
                                    validateForm={validateForm}
                                    {...commonStepProps}
                                    {...commonFormProps}
                                />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.storeSelect && (
                                <StoreSelectForm
                                    getFieldError={getFieldError}
                                    isCompliment={
                                        values.subject ===
                                        emailSubjects.compliment
                                    }
                                    onlineOrder={onlineOrder}
                                    selectedStoreName={store}
                                    setFieldValue={setFieldValue}
                                    {...commonStepProps}
                                />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.storeSelectOptional && (
                                <StoreSelectFormOptional
                                    getFieldError={getFieldError}
                                    selectedStoreName={store}
                                    setFieldValue={setFieldValue}
                                    {...commonStepProps}
                                />
                            )}
                            {currentStep.stepComponentName ===
                                stepComponentNames.messageForm && (
                                <MessageForm
                                    file={file}
                                    isAvgSubject={isAvgSubject}
                                    isValid={isValid}
                                    loading={loading}
                                    subject={values.subject}
                                    submitError={submitError}
                                    title={getDynamicTitle(
                                        step,
                                        values.subject
                                    )}
                                    onFileChange={setFile}
                                    {...commonFormProps}
                                />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </form>
            </AppContainer>
        </div>
    );
};

export const App = styled(BaseApp)`
    color: ${colors.black};

    ${AppContainer} {
        max-width: 800px;
        padding: 24px 12px 400px;
        margin: 0 auto;

        @media (${breakpoints.mediumMin}) {
            padding: 32px 24px 400px;
        }
    }

    h1 {
        margin: 0 0 24px;
        font-size: 24px;

        @media (${breakpoints.mediumMin}) {
            font-size: 32px;
        }
    }
`;
